import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
  SellerTransactionListResponse,
  SellerTransactionStatus,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseSellerTransactionsQueryParams = {
  storeSlug: string;
  /** Single status; omit for all. */
  status?: SellerTransactionStatus;
  /** Gateway slug; omit for all. */
  provider?: string;
  /** `yyyy-mm-dd` (inclusive). */
  from?: string;
  to?: string;
  /** Free text — order code or gateway transaction id. Debounced by the caller. */
  search?: string;
  limit?: number;
  offset?: number;
};

type NormalizedFilters = {
  status?: SellerTransactionStatus;
  provider?: string;
  from?: string;
  to?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

function normalizeFilters({
  status,
  provider,
  from,
  to,
  search,
  limit,
  offset,
}: Omit<UseSellerTransactionsQueryParams, "storeSlug">): NormalizedFilters {
  const normalizedProvider = provider?.trim();
  const normalizedSearch = search?.trim();

  return {
    status: status ?? undefined,
    provider:
      normalizedProvider && normalizedProvider.length > 0
        ? normalizedProvider
        : undefined,
    from: from && from.length > 0 ? from : undefined,
    to: to && to.length > 0 ? to : undefined,
    search:
      normalizedSearch && normalizedSearch.length > 0
        ? normalizedSearch
        : undefined,
    limit,
    offset,
  };
}

function buildPath(filters: NormalizedFilters): string {
  const query = new URLSearchParams();
  if (filters.status) {
    query.set("status", filters.status);
  }
  if (filters.provider) {
    query.set("provider", filters.provider);
  }
  if (filters.from) {
    query.set("from", filters.from);
  }
  if (filters.to) {
    query.set("to", filters.to);
  }
  if (filters.search) {
    query.set("search", filters.search);
  }
  if (filters.limit !== undefined) {
    query.set("limit", String(filters.limit));
  }
  if (filters.offset !== undefined) {
    query.set("offset", String(filters.offset));
  }

  const queryString = query.toString();
  return queryString.length > 0
    ? `${endpoints.seller.transactions}?${queryString}`
    : endpoints.seller.transactions;
}

type UseSellerTransactionsQueryResult = {
  data: SellerTransactionListResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useSellerTransactionsQuery(
  params: UseSellerTransactionsQueryParams,
): UseSellerTransactionsQueryResult {
  const { storeSlug, ...filters } = params;
  const normalizedFilters = normalizeFilters(filters);

  const query = useQuery({
    queryKey: queryKeys.seller.transactions(storeSlug, {
      status: normalizedFilters.status,
      provider: normalizedFilters.provider,
      from: normalizedFilters.from,
      to: normalizedFilters.to,
      search: normalizedFilters.search,
      limit: normalizedFilters.limit,
      offset: normalizedFilters.offset,
    }),
    queryFn: () =>
      apiJsonClient<SellerTransactionListResponse>(
        buildPath(normalizedFilters),
      ),
    enabled: storeSlug.length > 0,
    // Keep the current page on screen while a filter/page change loads, so the
    // list does not flash empty between pages.
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
